import React from 'react';
import snakeCase from 'lodash/snakeCase';
import useSWR from 'swr';
import DateType from '@/constants/DateType';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import useDebouncedEffect from '@/lib/hooks/useDebouncedEffect';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { convertDateTypeToDates, formatDateToString } from '@/utils/dates';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { fetcher } from '@/utils/services';

const useConstituents = (searchParams = {}, swrOptions) => {
  const ownerOrgId = useOwnerOrgId();
  const { data: customFieldData } = useCustomFields();
  const [swrKey, setSwrKey] = React.useState(null);

  useDebouncedEffect(
    () => {
      if (!ownerOrgId) return;

      const customFieldSlugs = (customFieldData || []).map(({ slug }) => slug);

      const {
        // These aren't being used with this version of search
        advancedFilters,
        filters,
        columns,

        // These are
        dateType,
        page,
        pageSize,
        userIds,
        ...rest
      } = searchParams;
      const params = Object.entries(rest).reduce((obj, [key, value]) => {
        // Omit null values
        if (value === null) return obj;

        let val = value;

        // Arrays are represented as a comma separated string
        if (Array.isArray(value)) {
          val = value.length ? value.join(',') : undefined;
        }

        if (value instanceof Date) {
          val = formatDateToString(value);
        }

        // We are manually snakeCase-ing the keys here and for pagination and
        // date parameters below instead of letting `joinNonEmptyParams` handle
        // it because the custom field slugs are kebab cased.
        const newKey = customFieldSlugs.includes(key)
          ? `custom_fields[${key}]`
          : snakeCase(key);

        return {
          ...obj,
          [newKey]: val,
        };
      }, {});

      if (page && pageSize) {
        params.page_from = (page - 1) * pageSize;
        params.page_size = pageSize;
      }

      if (dateType && dateType !== DateType.CUSTOM_DATES) {
        const [startDate, endDate] = convertDateTypeToDates(dateType);
        params.start_date = formatDateToString(startDate);
        params.end_date = formatDateToString(endDate);
      }

      switch (userIds) {
        case 'all':
          break;
        case 'assigned':
          params.has_assignees = true;
          break;
        case 'unassigned':
          params.has_assignees = false;
          break;
        default:
          params.user_ids = [userIds];
          break;
      }

      setSwrKey(
        `orgs/${ownerOrgId}/constituents/search?${joinNonEmptyParams(
          params,
          false,
        )}`,
      );
    },
    [customFieldData, ownerOrgId, searchParams],
    200,
  );

  return useSWR(swrKey, fetcher, swrOptions);
};

export default useConstituents;
